
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Container, Row, Col, Breadcrumb, Card, Spinner, Alert } from "react-bootstrap";
import { Link } from "react-router-dom";
import { 
  HomeOutlined, 
  TeamOutlined, 
  CheckCircleOutlined, 
  ClockCircleOutlined, 
  WarningOutlined,
  TrophyOutlined,
  RiseOutlined,
  SearchOutlined,
} from "@ant-design/icons";

import { Statistic, Progress, Tag, Input, Empty } from "antd";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar, Pie, Doughnut } from "react-chartjs-2";
import { fetchAdminOverview, fetchTopPerformers } from "@/store/dashboardApi/dashboardThunk";
import { fetchUsersWithStatistics } from "@/store/adminApi/adminThunk";
import formatDate from "@/utils/formatDate";
import { CalendarView, TaskDateModal } from "@/components/Calendar";
import UserStatisticsModal from "@/components/UserStatisticsModal";
import {fetchWorkloadDistribution} from "../store/analyticsApi/analyticsThunk.js";

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const AdminDashboard = () => {
  const dispatch = useDispatch();
  const [topPerformersPeriod, setTopPerformersPeriod] = useState("month");
  const [selectedDate, setSelectedDate] = useState(null);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showUserStatsModal, setShowUserStatsModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  const {
    adminOverview,
    topPerformers,
    adminOverviewLoading,
    topPerformersLoading,
    adminOverviewError,
  } = useSelector((state) => state.dashboard);

  const {
    workloadDistribution,
    workloadDistributionLoading,
  } = useSelector((state) => state.analytics);

  const { usersWithStatistics, statisticsLoading } = useSelector(
    (state) => state.adminApi
  );

  useEffect(() => {
    dispatch(fetchAdminOverview());
    dispatch(fetchTopPerformers({ period: topPerformersPeriod, limit: 10 }));
    dispatch(fetchUsersWithStatistics());
    dispatch(fetchWorkloadDistribution());

  }, [dispatch, topPerformersPeriod]);

  console.log(adminOverview, 'adminover');
  

  // Extract data from adminOverview
  const healthMetrics = adminOverview?.health_metrics;
  const taskStatusCounts = adminOverview?.task_status_counts;
  const taskPerformance = adminOverview?.task_performance;
  const recentActivity = adminOverview?.recent_activity || [];

  // Filter users based on search query
  const filteredUsers = usersWithStatistics?.filter(user => 
    user.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.login?.toLowerCase().includes(searchQuery.toLowerCase())
  ) || [];

  // Prepare chart data for task status distribution
  const taskStatusChartData = taskStatusCounts ? {
    labels: ["Kutilayotgan", "Jarayonda", "Topshirilgan", "Tasdiqlangan", "Rad etilgan"],
    datasets: [
      {
        label: "Topshiriqlar soni",
        data: [
          taskStatusCounts.pending,
          taskStatusCounts.in_progress,
          taskStatusCounts.submitted,
          taskStatusCounts.approved,
          taskStatusCounts.rejected,
        ],
        backgroundColor: [
          "rgba(255, 193, 7, 0.8)",
          "rgba(33, 150, 243, 0.8)",
          "rgba(156, 39, 176, 0.8)",
          "rgba(76, 175, 80, 0.8)",
          "rgba(244, 67, 54, 0.8)",
        ],
        borderWidth: 1,
      },
    ],
  } : null;

  // Prepare chart data for top performers
  const topPerformersChartData = topPerformers?.length > 0 ? {
    labels: topPerformers.map(p => p.name),
    datasets: [
      {
        label: "Bajarilgan topshiriqlar",
        data: topPerformers.map(p => p.tasks_completed),
        backgroundColor: "rgba(76, 175, 80, 0.6)",
        borderColor: "rgba(76, 175, 80, 1)",
        borderWidth: 1,
      },
    ],
  } : null;

   // Workload Distribution Chart Data
  const workloadByStatusChartData = workloadDistribution?.by_status ? {
    labels: ["Yengil", "O'rtacha", "O'gir", "Yuqori"],
    datasets: [{
      data: [
        workloadDistribution.by_status.light || 0,
        workloadDistribution.by_status.normal || 0,
        workloadDistribution.by_status.heavy || 0,
        workloadDistribution.by_status.overloaded || 0,
      ],
      backgroundColor: [
        "rgba(76, 175, 80, 0.8)",
        "rgba(33, 150, 243, 0.8)",
        "rgba(255, 152, 0, 0.8)",
        "rgba(244, 67, 54, 0.8)",
      ],
    }],
  } : null;

  // Activity type mapping
  const activityTypeMap = {
    login: "Tizimga kirdi",
    task_create: "Topshiriq yaratdi",
    task_complete: "Topshiriq bajardi",
    task_submit: "Topshiriq topshirdi",
    task_approve: "Topshiriq tasdiqladi",
    task_reject: "Topshiriq rad etdi",
    task_start: "Topshiriqni boshladi",
  };

  const getActivityIcon = (type) => {
    const iconMap = {
      login: "fa fa-sign-in",
      task_create: "fa fa-plus-circle",
      task_complete: "fa fa-check-circle",
      task_submit: "fa fa-paper-plane",
      task_approve: "fa fa-thumbs-up",
      task_reject: "fa fa-thumbs-down",
      task_start: "fa fa-play-circle",
    };
    return iconMap[type] || "fa fa-circle";
  };

  // Calendar handlers
  const handleDateClick = (date) => {
    setSelectedDate(date);
    setShowTaskModal(true);
  };

  const handleModalClose = () => {
    setShowTaskModal(false);
    setSelectedDate(null);
  };

  const handleUserClick = (userId) => {
    setSelectedUserId(userId);
    setShowUserStatsModal(true);
  };

  const handleUserStatsModalClose = () => {
    setShowUserStatsModal(false);
    setSelectedUserId(null);
  };

  if (adminOverviewLoading && !adminOverview) {
    return (
      <Container fluid className="p-4 d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
        <Spinner animation="border" variant="primary" />
      </Container>
    );
  }

  if (adminOverviewError) {
    return (
      <Container fluid className="p-4">
        <Alert variant="danger">
          <Alert.Heading>Xatolik yuz berdi</Alert.Heading>
          <p>{adminOverviewError}</p>
        </Alert>
      </Container>
    );
  }

  console.log(recentActivity, 'recent');
  

  return (
    <Container fluid className="p-4">
      {/* Breadcrumb */}
      <Breadcrumb>
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: "/" }}>
          <HomeOutlined />
        </Breadcrumb.Item>
        <Breadcrumb.Item active>Admin Dashboard</Breadcrumb.Item>
      </Breadcrumb>

      {/* System Health Metrics */}
      <Row className="g-3 mb-4">
        <Col xs={12} sm={6} lg={3}>
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <p className="text-muted mb-1" style={{ fontSize: "13px" }}>Jami foydalanuvchilar</p>
                  <h3 className="mb-0" style={{ color: "#1890ff" }}>{healthMetrics?.total_users || 0}</h3>
                </div>
                <div style={{ fontSize: "32px", opacity: 0.2 }}>
                  <TeamOutlined />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} sm={6} lg={3}>
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <p className="text-muted mb-1" style={{ fontSize: "13px" }}>Bugun faol</p>
                  <h3 className="mb-0" style={{ color: "#52c41a" }}>{healthMetrics?.active_users_today || 0}</h3>
                </div>
                <div style={{ fontSize: "32px", opacity: 0.2 }}>
                  <TeamOutlined />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} sm={6} lg={3}>
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <p className="text-muted mb-1" style={{ fontSize: "13px" }}>Jami topshiriqlar</p>
                  <h3 className="mb-0" style={{ color: "#722ed1" }}>{healthMetrics?.total_tasks || 0}</h3>
                </div>
                <div style={{ fontSize: "32px", opacity: 0.2 }}>
                  <CheckCircleOutlined />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} sm={6} lg={3}>
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <p className="text-muted mb-1" style={{ fontSize: "13px" }}>Jami tadbirlar</p>
                  <h3 className="mb-0" style={{ color: "#fa8c16" }}>{healthMetrics?.total_events || 0}</h3>
                </div>
                <div style={{ fontSize: "32px", opacity: 0.2 }}>
                  <ClockCircleOutlined />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Task Performance Metrics */}
      <Row className="g-3 mb-4">
        <Col xs={12} lg={8}>
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex align-items-center mb-3">
                <TrophyOutlined style={{ fontSize: "20px", color: "#faad14", marginRight: "8px" }} />
                <h5 className="mb-0">Topshiriq ishlash ko'rsatkichlari</h5>
              </div>
              
              <Row className="g-3">
                <Col xs={12} md={6}>
                  <div className="p-3 border rounded">
                    <p className="text-muted mb-2 small">Bajarilish foizi</p>
                    <Progress
                      percent={Math.round(taskPerformance?.completion_rate || 0)}
                      strokeColor="#52c41a"
                      strokeWidth={12}
                    />
                  </div>
                </Col>
                <Col xs={12} md={6}>
                  <div className="p-3 border rounded">
                    <p className="text-muted mb-2 small">Muddatida bajarilish</p>
                    <Progress
                      percent={Math.round(taskPerformance?.on_time_completion_rate || 0)}
                      strokeColor="#1890ff"
                      strokeWidth={12}
                    />
                  </div>
                </Col>
                <Col xs={6}>
                  <div className="text-center p-3 border rounded">
                    <ClockCircleOutlined style={{ fontSize: "24px", color: "#faad14" }} />
                    <h4 className="mt-2 mb-0">{taskPerformance?.avg_completion_time_hours?.toFixed(1) || "0"}</h4>
                    <small className="text-muted">O'rtacha soat</small>
                  </div>
                </Col>
                <Col xs={6}>
                  <div className="text-center p-3 border rounded">
                    <WarningOutlined style={{ fontSize: "24px", color: "#ff4d4f" }} />
                    <h4 className="mt-2 mb-0">{taskPerformance?.overdue_tasks || 0}</h4>
                    <small className="text-muted">Muddati o'tgan</small>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>

        {/* Task Status Distribution Chart */}
        <Col xs={12} lg={4}>
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body>
              <h5 className="mb-3">Topshiriqlar taqsimoti</h5>
              {taskStatusChartData && (
                <div style={{ height: "250px" }}>
                  <Pie
                    data={taskStatusChartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          position: "bottom",
                          labels: {
                            font: {
                              size: 11
                            }
                          }
                        },
                      },
                    }}
                  />
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

       {/* Workload Distribution Tab */}
          <Row className="g-3 mb-4">
            <Col xs={12} lg={6}>
              <Card className="h-100">
                <Card.Body>
                  <h5 className="mb-3">Ish yuki taqsimoti</h5>
                  {workloadDistributionLoading ? (
                    <div className="text-center py-5">
                      <Spinner animation="border" />
                    </div>
                  ) : workloadByStatusChartData ? (
                    <div style={{ height: "300px" }}>
                      <Doughnut
                        data={workloadByStatusChartData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: {
                            legend: {
                              position: "bottom",
                            },
                          },
                        }}
                      />
                    </div>
                  ) : (
                    <p className="text-muted">Ma'lumot mavjud emas</p>
                  )}
                </Card.Body>
              </Card>
            </Col>
            <Col xs={12} lg={6}>
              <Card className="h-100">
                <Card.Body>
                  <h5 className="mb-3">Eng ko'p vazifa yuklangan xodimlar</h5>
                  {workloadDistributionLoading ? (
                    <div className="text-center py-5">
                      <Spinner animation="border" />
                    </div>
                  ) : workloadDistribution?.top_loaded_users?.length > 0 ? (
                    <div style={{ maxHeight: "350px", overflowY: "auto" }}>
                      {workloadDistribution.top_loaded_users.slice(0, 10).map((user, idx) => (
                        <div key={user.user_id} className="mb-3 pb-3 border-bottom">
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <h6 className="mb-1">#{idx + 1} {user.name}</h6>
                              <small className="text-muted">{user.active_tasks} faol topshiriq</small>
                            </div>
                            <Tag color={
                              user.workload_status === "overloaded" ? "red" :
                              user.workload_status === "heavy" ? "orange" :
                              user.workload_status === "normal" ? "blue" : "green"
                            }>
                              {user.workload_status === "overloaded" ? "Yuqori" :
   user.workload_status === "heavy" ? "Og'ir" :
   user.workload_status === "normal" ? "O'rtacha" : "Yengil"}
                            </Tag>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted">Ma'lumot mavjud emas</p>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>

      {/* User Statistics Table */}
      <Row className="g-3 mb-4">
        <Col xs={12}>
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">Foydalanuvchilar statistikasi</h5>
                <Input
                  placeholder="Qidirish..."
                  prefix={<SearchOutlined />}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{ width: 300 }}
                  allowClear
                />
              </div>
              {statisticsLoading ? (
                <div className="text-center py-3">
                  <Spinner animation="border" size="sm" />
                </div>
              ) : filteredUsers.length > 0 ? (
                <div style={{ maxHeight: "700px", overflowY: "auto", overflowX: "auto" }}>
                  <table className="table table-hover">
                    <thead style={{ position: "sticky", top: 0, backgroundColor: "#fff", zIndex: 1 }}>
                      <tr>
                        <th>Foydalanuvchi</th>
                        <th className="text-center">Jami</th>
                        <th className="text-center">Bajarilgan</th>
                        <th className="text-center">Muddati o'tgan</th>
                        <th className="text-center">Bajarilish %</th>
                        <th className="text-center">Reyting</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUsers.map((user) => (
                        <tr
                          key={user.id}
                          onClick={() => handleUserClick(user.id)}
                          style={{ cursor: "pointer" }}
                          className="hover-bg-light"
                        >
                          <td>
                            <div className="d-flex align-items-center">
                              {user.picture ? (
                                <img
                                  src={user.picture}
                                  alt={user.name}
                                  className="rounded-circle me-2"
                                  style={{
                                    width: "32px",
                                    height: "32px",
                                    objectFit: "cover",
                                  }}
                                />
                              ) : (
                                <div
                                  className="bg-secondary text-white rounded-circle me-2 d-flex align-items-center justify-content-center"
                                  style={{
                                    width: "32px",
                                    height: "32px",
                                    fontSize: "0.9rem",
                                  }}
                                >
                                  {user.name?.charAt(0) || "U"}
                                </div>
                              )}
                              <span>{user.name || "N/A"}</span>
                            </div>
                          </td>
                          <td className="text-center">
                            {user.statistics?.tasks_assigned || 0}
                          </td>
                          <td className="text-center">
                            <Tag color="success">
                              {user.statistics?.tasks_completed || 0}
                            </Tag>
                          </td>
                          <td className="text-center">
                            {user.statistics?.tasks_overdue > 0 ? (
                              <Tag color="error">
                                {user.statistics.tasks_overdue}
                              </Tag>
                            ) : (
                              <Tag color="default">0</Tag>
                            )}
                          </td>
                          <td className="text-center">
                            <Progress
                              type="circle"
                              percent={Math.round(
                                user.statistics?.completion_rate || 0
                              )}
                              width={40}
                              strokeColor="#52c41a"
                            />
                          </td>
                          <td className="text-center">
                            {user.statistics?.avg_rating ? (
                              <>
                                {user.statistics.avg_rating.toFixed(1)}{" "}
                                <i className="fa fa-star text-warning"></i>
                              </>
                            ) : (
                              "N/A"
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <Empty 
                  description={searchQuery ? "Foydalanuvchi topilmadi" : "Ma'lumot mavjud emas"} 
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  style={{ padding: "60px 0" }}
                />
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Calendar View */}
      <Row className="g-3 mb-4">
        <Col xs={12}>
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <h5 className="mb-3">Topshiriqlar kalendari</h5>
              <CalendarView onDateClick={handleDateClick} isAdmin={true} />
            </Card.Body>
          </Card>
        </Col>
      </Row>

        {/* Top Performers */}
      <Row className="g-3 mb-4">
        <Col xs={12}>
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">
                  <RiseOutlined style={{ marginRight: "8px", color: "#52c41a" }} />
                  Eng yaxshi ishchilar
                </h5>
                <div>
                  <button
                    className={`btn btn-sm ${topPerformersPeriod === "week" ? "btn-primary" : "btn-outline-primary"} me-2`}
                    onClick={() => setTopPerformersPeriod("week")}
                  >
                    Hafta
                  </button>
                  <button
                    className={`btn btn-sm ${topPerformersPeriod === "month" ? "btn-primary" : "btn-outline-primary"} me-2`}
                    onClick={() => setTopPerformersPeriod("month")}
                  >
                    Oy
                  </button>
                  <button
                    className={`btn btn-sm ${topPerformersPeriod === "year" ? "btn-primary" : "btn-outline-primary"}`}
                    onClick={() => setTopPerformersPeriod("year")}
                  >
                    Yil
                  </button>
                </div>
              </div>
              {topPerformersLoading ? (
                <div className="text-center py-3">
                  <Spinner animation="border" size="sm" />
                </div>
              ) : topPerformersChartData ? (
                <div style={{ height: "300px" }}>
                  <Bar
                    data={topPerformersChartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          display: false,
                        },
                      },
                      scales: {
                        y: {
                          beginAtZero: true,
                          ticks: {
                            stepSize: 1,
                          },
                        },
                      },
                    }}
                  />
                </div>
              ) : (
                <Empty description="Ma'lumot mavjud emas" image={Empty.PRESENTED_IMAGE_SIMPLE} />
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Recent Activity Feed */}
      <Row className="g-3">
        <Col xs={12}>
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <h5 className="mb-3">So'nggi faoliyat</h5>
              {recentActivity.length > 0 ? (
                <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                  {recentActivity.map((activity, idx) => (
                    <div key={activity.id || idx} className="p-3 mb-2 border-bottom">
                      <div className="d-flex align-items-start">
                        <div className="me-3">
                          <i className={`${getActivityIcon(activity.activity_type)} text-primary`} style={{ fontSize: "1.5rem" }}></i>
                        </div>
                        <div className="flex-grow-1">
                          <p className="mb-1 fw-medium">
                            <strong>{activity.user_name || "Foydalanuvchi"}</strong>
                            {" "}{activityTypeMap[activity.activity_type] || activity.activity_type}
                          </p>
                          <small className="text-muted">
                            {formatDate(activity.timestamp)}
                          </small>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <Empty 
                  description="Faoliyat mavjud emas" 
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  style={{ padding: "60px 0" }}
                />
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Task Date Modal */}
      <TaskDateModal
        visible={showTaskModal}
        onClose={handleModalClose}
        selectedDate={selectedDate}
      />

      {/* User Statistics Modal */}
      <UserStatisticsModal
        show={showUserStatsModal}
        onHide={handleUserStatsModalClose}
        userId={selectedUserId}
      />

      <style jsx>{`
        .hover-bg-light:hover {
          background-color: #f8f9fa;
        }
      `}</style>
    </Container>
  );
};

export default AdminDashboard;